import React, { useMemo, useRef, useState } from 'react';

import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import RemoveIcon from '@mui/icons-material/Remove';
import { Button, Checkbox, Dialog, FormControlLabel, IconButton, Link, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import clsx from 'clsx';
import { useMutation, useQueryClient } from 'react-query';

import { classicExerciseQueryKey } from 'api/rest/exercises/classicExerciseQuery';
import { exerciseIdForRestApi } from 'api/rest/exercises/exerciseIdForRestApi';
import exercisesApi from 'api/rest/exercises/exercises';
import type { ClassicExercise, Exercise, ExerciseIsDone, ExerciseUserMark } from 'api/rest/exercises/exercises.types';
import { ExerciseFileTypes, toExerciseId } from 'api/rest/exercises/exercises.types';
import easyImageSrc from 'assets/icons/feedback/easy.png';
import hardImageSrc from 'assets/icons/feedback/hard.png';
import veryHardImageSrc from 'assets/icons/feedback/very_hard.png';
import ImageWithLoaderWrapper from 'components/ImageWithLoaderWrapper/ImageWithLoaderWrapper';
import feedbackConsts from 'constants/dictionaries/feedbacks';
import messages from 'constants/dictionaries/messages';
import MUTATION from 'constants/mutationsKeys';
import QUERY from 'constants/queryKeys';
import { useAuthContext } from 'context/AuthContext';
import { useFiltersCountContext } from 'context/FiltersCountContext';
import { useGlobalDialogContext } from 'context/GlobalDialogContext';
import { useUserLogContext } from 'context/UserLogContext/UserLogContext';
import useCommitError from 'services/common/useCommitError/useCommitError';
import useFeatureChecker from 'services/common/useFeatureChecker/useFeatureChecker';
import useNavigation from 'services/exercises/useNavigation/useNavigation';
import useUserPreferencesStore from 'storages/userPreferences';

import useStyles, { useButtonStyles } from './MarkExercise.styles';

type ExercisesFeedbackButtonProps = {
  label: string;
  color: string;
  onClick: () => void;
  selected?: boolean;
  src?: string;
  Icon?: typeof CheckIcon;
};

const ExercisesFeedbackButton: React.FC<ExercisesFeedbackButtonProps> = ({
  label,
  color,
  onClick,
  selected = false,
  src,
  Icon,
}) => {
  const { classes } = useButtonStyles({ color });

  const getStartIcon = () => {
    if (src) {
      return <img alt='' className={classes.iconImage} src={src} />;
    }

    if (Icon) {
      return <Icon className={classes.icon} />;
    }

    return undefined;
  };

  return (
    <Button
      className={clsx(classes.button, selected && classes.buttonSelected)}
      onClick={onClick}
      startIcon={getStartIcon()}
      variant='outlined'
    >
      {label}
    </Button>
  );
};

type MarkExerciseProps = {
  exerciseToMark: string;
  withNext: boolean;
  /** @deprecated Apollo cache path removed; kept for call-site compatibility. */
  cacheStrategy?: 'apollo' | 'rq';
  initialMark?: ExerciseUserMark | null;
  initialIsDone?: ExerciseIsDone | null;
};

const IS_DONE_OPTIONS: { value: ExerciseIsDone; label: string; Icon: typeof CheckIcon }[] = [
  { value: 'yes', label: 'Tak', Icon: CheckIcon },
  { value: 'no', label: 'Nie', Icon: CloseIcon },
  { value: 'not_sure', label: 'Nie jestem pewien', Icon: RemoveIcon },
];

const getAnswerImageUrls = (files?: Array<{ fileType?: string; fileIndex?: number; croppedPngFile?: string }> | null): string[] => {
  if (!files?.length) return [];

  return files
    .filter(file => file.fileType === ExerciseFileTypes.answer)
    .sort((a, b) => (a.fileIndex ?? 0) - (b.fileIndex ?? 0))
    .map(file => file.croppedPngFile)
    .filter((url): url is string => Boolean(url));
};

type AnswerKeyImagesProps = {
  urls: string[];
  loading?: boolean;
  zoomed?: boolean;
  setZoomed?: (zoomed: boolean) => void;
};

const AnswerKeyImages = ImageWithLoaderWrapper as React.ComponentType<AnswerKeyImagesProps>;

/* eslint-disable @typescript-eslint/ban-ts-comment */
const MarkExercise: React.FC<MarkExerciseProps> = ({ exerciseToMark, withNext, initialMark, initialIsDone }) => {
  const { logEvent } = useUserLogContext();
  const { next } = useNavigation();
  const theme = useTheme();
  const { useDataStore, isSheet, feature } = useFeatureChecker();
  const dataStore = useDataStore();
  const { currentExercise, setExerciseMark, setCurrentExercise } = dataStore;
  // @ts-ignore
  const storeMark = dataStore.mark as ExerciseUserMark | null;
  // @ts-ignore
  const { isStudent } = useAuthContext();
  // @ts-ignore
  const { hideMarking, setHideMarking } = useUserPreferencesStore();
  // @ts-ignore
  const { closeGlobalDialog: closeModalMark } = useGlobalDialogContext();
  // @ts-ignore
  const { updaters } = useFiltersCountContext();
  const commitError = useCommitError();
  const queryClient = useQueryClient();

  // @ts-ignore — currentExercise may carry the ClassicExercise userMark shape at runtime
  const rawUserMark = (currentExercise?.userMark ?? null) as
    | { mark?: ExerciseUserMark | null; isDone?: ExerciseIsDone | null }
    | ExerciseUserMark
    | null;

  const resolveInitialMark = (): ExerciseUserMark | null => {
    if (initialMark !== undefined) return initialMark;
    if (storeMark) return storeMark;
    if (rawUserMark === null || rawUserMark === undefined) return null;
    if (typeof rawUserMark === 'string') return rawUserMark;
    return (rawUserMark as { mark?: ExerciseUserMark | null }).mark ?? null;
  };

  const resolveInitialIsDone = (): ExerciseIsDone | null => {
    if (initialIsDone !== undefined) return initialIsDone;
    if (rawUserMark !== null && typeof rawUserMark === 'object') {
      const isDone = (rawUserMark as { isDone?: ExerciseIsDone | null }).isDone;
      if (isDone) return isDone;
    }
    // @ts-ignore — Exercise shape carries top-level isDone at runtime
    return (currentExercise?.isDone as ExerciseIsDone | null) ?? null;
  };

  const initialMarkRef = useRef<ExerciseUserMark | null>(resolveInitialMark());
  const initialIsDoneRef = useRef<ExerciseIsDone | null>(resolveInitialIsDone());

  const [selectedMark, setSelectedMark] = useState<ExerciseUserMark | null>(initialMarkRef.current);
  const [selectedIsDone, setSelectedIsDone] = useState<ExerciseIsDone | null>(initialIsDoneRef.current);
  const [isAnswerPreviewOpen, setIsAnswerPreviewOpen] = useState(false);
  const [isAnswerZoomed, setIsAnswerZoomed] = useState(false);

  const answerUrls = useMemo(() => {
    const uuid = exerciseIdForRestApi(exerciseToMark);
    const cached = uuid ? queryClient.getQueryData<ClassicExercise>(classicExerciseQueryKey(uuid)) : undefined;
    const currentMatchesMarkedExercise =
      !!currentExercise &&
      (String(currentExercise.id) === String(exerciseToMark) || exerciseIdForRestApi(currentExercise.id) === uuid);
    const files = (currentMatchesMarkedExercise ? currentExercise?.files : undefined) ?? cached?.files;
    return getAnswerImageUrls(files);
  }, [currentExercise, exerciseToMark, queryClient]);

  const markMutation = useMutation(MUTATION.MARK_EXERCISE, async (mark: ExerciseUserMark | null) => {
    const uuid = exerciseIdForRestApi(exerciseToMark);
    if (!uuid) throw new Error('Invalid exercise id for mark');
    return exercisesApi.mark(toExerciseId(uuid))({ mark });
  });

  const isDoneMutation = useMutation(MUTATION.SET_EXERCISE_IS_DONE, async (isDone: ExerciseIsDone | null) => {
    const uuid = exerciseIdForRestApi(exerciseToMark);
    if (!uuid) throw new Error('Invalid exercise id for is_done');
    return exercisesApi.setIsDone(toExerciseId(uuid))({ isDone });
  });

  // @ts-ignore
  const onHideForLaterCheck = ({ target }) => {
    logEvent('ignore-exercise-mark-click');
    setHideMarking(target.checked);
  };

  const patchCaches = (uuid: string, nextUserMark: { id: number; mark: ExerciseUserMark | null; isDone: ExerciseIsDone | null }) => {
    queryClient.setQueryData(classicExerciseQueryKey(uuid), (old: ClassicExercise | undefined) =>
      old ? { ...old, userMark: nextUserMark } : old,
    );
    queryClient.setQueriesData<Exercise | undefined>([QUERY.GET_SINGLE_EXERCISE], old => {
      if (!old) return old;
      const oldId = String(old.id ?? '');
      if (oldId !== uuid && oldId !== String(exerciseToMark)) return old;
      return { ...old, userMark: nextUserMark.mark, isDone: nextUserMark.isDone };
    });
  };

  const persistInBackground = (nextMark: ExerciseUserMark | null, nextIsDone: ExerciseIsDone | null) => {
    if (!exerciseToMark) {
      commitError(
        { message: 'missing exerciseToMark' },
        messages.ERROR.SAVING('oznaczenia zadania'),
        'ExercisesMark: persist (missing exerciseToMark)',
      );
      return;
    }

    const markChanged = nextMark !== initialMarkRef.current;
    const isDoneChanged = nextIsDone !== initialIsDoneRef.current;

    if (!markChanged && !isDoneChanged) {
      return;
    }

    const uuid = exerciseIdForRestApi(exerciseToMark);
    const currentUuid = exerciseIdForRestApi(currentExercise?.id);
    const isMarkingCurrentExercise =
      (uuid && currentUuid && uuid === currentUuid) || String(currentExercise?.id ?? '') === String(exerciseToMark);
    const existingUserMark = isMarkingCurrentExercise ? currentExercise?.userMark : undefined;
    const existingUserMarkId = existingUserMark && typeof existingUserMark === 'object' ? existingUserMark.id : undefined;
    const nextUserMark = {
      id: existingUserMarkId ?? 0,
      mark: nextMark,
      isDone: nextIsDone,
    };

    if (uuid) {
      patchCaches(uuid, nextUserMark);
    }
    if (typeof setCurrentExercise === 'function' && currentExercise?.files && isMarkingCurrentExercise) {
      const nextStoredUserMark =
        existingUserMark && typeof existingUserMark === 'object' ? { ...existingUserMark, ...nextUserMark } : nextUserMark;
      setCurrentExercise({
        ...currentExercise,
        mark: nextUserMark.mark,
        isDone: nextUserMark.isDone,
        userMark: nextStoredUserMark,
      });
    }
    if (markChanged && !withNext) setExerciseMark(nextMark);

    const requests: Promise<unknown>[] = [];
    if (markChanged) requests.push(markMutation.mutateAsync(nextMark));
    if (isDoneChanged) requests.push(isDoneMutation.mutateAsync(nextIsDone));

    // Refetch filter counts only after the backend has the new mark / is_done —
    // refetching earlier races the write and leaves stale zeros in the filters UI.
    void Promise.all(requests)
      .then(() => {
        if (markChanged) updaters?.getMarksCount?.();
        if (isDoneChanged) updaters?.getIsDoneCount?.();
      })
      .catch(error => {
        commitError(error as Error, messages.ERROR.SAVING('oznaczenia zadania'), 'ExercisesMark: persist');
      });
  };

  const commitAndClose = (nextMark: ExerciseUserMark | null, nextIsDone: ExerciseIsDone | null) => {
    logEvent('mark-exercise', {
      mark: nextMark,
      isDone: nextIsDone,
      exercise: currentExercise?.index,
      feature,
    });
    persistInBackground(nextMark, nextIsDone);
    if (withNext) next();
    closeModalMark();
  };

  const onMarkClick = (mark: ExerciseUserMark) => () => {
    setSelectedMark(mark);
  };

  const onIsDoneClick = (isDone: ExerciseIsDone) => () => {
    const nextIsDone = selectedIsDone === isDone ? null : isDone;
    setSelectedIsDone(nextIsDone);
  };

  const okHandler = () => commitAndClose(selectedMark, selectedIsDone);

  const skipHandler = () => {
    logEvent('mark-exercise', {
      mark: isStudent ? 'zamknij' : 'pomiń',
      exercise: currentExercise?.index,
      feature,
    });
    if (withNext) next();
    closeModalMark();
  };

  const { EASY, HARD, EXPLAIN } = feedbackConsts;
  const { classes } = useStyles({ hideForLater: isSheet });

  const closeAnswerPreview = () => {
    setIsAnswerPreviewOpen(false);
    setIsAnswerZoomed(false);
  };

  const skipLabel = isStudent && !withNext ? 'Zamknij' : 'Pomiń';

  return (
    <div className={classes.wrapper}>
      <div className={classes.scrollArea}>
        <section className={classes.section}>
          <div className={classes.sectionHeader}>
            <Typography variant='h4'>Poziom trudności</Typography>
            <Typography className={classes.sectionSubtitle} variant='body2'>
              Oceń, jak trudne było to zadanie. Pomoże Ci to później wrócić do tych najbardziej wymagających.
            </Typography>
          </div>
          <div className={classes.row}>
            <ExercisesFeedbackButton
              color={theme.palette.primary.main}
              label={EASY.name}
              selected={selectedMark === EASY.id}
              src={easyImageSrc}
              onClick={onMarkClick(EASY.id as ExerciseUserMark)}
            />
            <ExercisesFeedbackButton
              color={theme.palette.custom.orange}
              label={HARD.name}
              selected={selectedMark === HARD.id}
              src={hardImageSrc}
              onClick={onMarkClick(HARD.id as ExerciseUserMark)}
            />
            <ExercisesFeedbackButton
              color={theme.palette.custom.red}
              label={EXPLAIN.name}
              selected={selectedMark === EXPLAIN.id}
              src={veryHardImageSrc}
              onClick={onMarkClick(EXPLAIN.id as ExerciseUserMark)}
            />
          </div>
        </section>

        <section className={classes.section}>
          <div className={classes.sectionHeader}>
            <Typography variant='h4'>Poprawność rozwiązania</Typography>
            <Typography className={classes.sectionSubtitle} component='div' variant='body2'>
              Sprawdź swoją odpowiedź z{' '}
              <Link className={classes.keyLink} component='button' type='button' underline='always' onClick={() => setIsAnswerPreviewOpen(true)}>
                odpowiedzią
              </Link>{' '}
              i zaznacz czy udało Ci się rozwiązać poprawnie
            </Typography>
          </div>
          <div className={classes.row}>
            {IS_DONE_OPTIONS.map(({ value, label, Icon }) => (
              <ExercisesFeedbackButton
                key={value}
                Icon={Icon}
                label={label}
                selected={selectedIsDone === value}
                onClick={onIsDoneClick(value)}
              />
            ))}
          </div>
        </section>

        {isSheet && !isStudent && (
          <FormControlLabel
            key='hideForLater'
            className={classes.hideForLater}
            control={<Checkbox checked={hideMarking} name='hideForLater' onChange={onHideForLaterCheck} />}
            label={<span className={classes.checkboxLabel}>Nie pytaj mnie o to więcej w tym arkuszu</span>}
          />
        )}
      </div>

      <div className={classes.footer}>
        <Button className={classes.footerButton} color='primary' size='small' variant='outlined' onClick={skipHandler}>
          {skipLabel}
        </Button>
        <Button className={classes.footerButton} color='primary' size='small' variant='contained' onClick={okHandler}>
          OK
        </Button>
      </div>

      <Dialog
        fullWidth
        maxWidth='md'
        onClose={closeAnswerPreview}
        open={isAnswerPreviewOpen}
        PaperProps={{ className: classes.answerPaper }}
        scroll='paper'
        sx={{ zIndex: theme => theme.zIndex.modal + 2 }}
      >
        <div className={classes.answerHeader}>
          <Typography className={classes.answerTitle} variant='h4'>
            Odpowiedź
          </Typography>
          <IconButton aria-label='Zamknij' onClick={closeAnswerPreview} size='small'>
            <CloseIcon />
          </IconButton>
        </div>
        <div className={classes.answerContent}>
          {answerUrls.length ? (
            <div className={classes.answerImageFrame}>
              <AnswerKeyImages loading={false} setZoomed={setIsAnswerZoomed} urls={answerUrls} zoomed={isAnswerZoomed} />
            </div>
          ) : (
            <Typography className={classes.answerEmpty} variant='body2'>
              Brak odpowiedzi dla tego zadania.
            </Typography>
          )}
        </div>
      </Dialog>
    </div>
  );
};

export default MarkExercise;
